import { Injectable, inject } from "@angular/core";
import { UploadService } from "@shared-api/upload/upload.service";
import { EMPTY, Subject, catchError, map, switchMap } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FileData, FileDataUpload } from "@shared-interfaces/shared-file";
import { UploadProcessResponse } from "@shared-interfaces/shared-upload";
import { environment } from "@environments/environment";

interface SignedUrlResponse {
  file: FileDataUpload;
  uploadResponse: UploadProcessResponse;
}

@Injectable({
  providedIn: "root",
})
export class UploadComponentService {
  private uploadService = inject(UploadService);

  // source
  processes = new Subject<UploadProcessResponse>();

  // Actions
  private error$ = new Subject<any>();

  uploadFile$ = new Subject<FileDataUpload>();
  private uploading$ = this.uploadFile$.pipe(
    switchMap((file: FileDataUpload) => {
      return this.getSignedUrl(file).pipe(
        map(
          (uploadResponse) => ({ file, uploadResponse }) as SignedUrlResponse
        ),
        catchError((err) => {
          console.error(err);
          this.error$.next(err);
          return EMPTY;
        })
      );
    })
  );

  deleteFile$ = new Subject<FileData & {workflowId: string}>();

  private signedUrlUpload$ = new Subject<SignedUrlResponse>();
  private signedUrl$ = this.signedUrlUpload$.pipe(
    switchMap((response: SignedUrlResponse) => {
      return this.uploadFileWithSignedUrl(
        response.file.file,
        response.uploadResponse.signedUrl.url
      ).pipe(
        map((signedUrlResponse) => ({ response, signedUrlResponse })),
        catchError((err) => {
          console.error(err);
          this.error$.next(err);
          return EMPTY;
        })
      );
    })
  );

  constructor() {
    this.uploading$.pipe(takeUntilDestroyed()).subscribe({
      next: (response: SignedUrlResponse) => {
        this.processes.next(response.uploadResponse);
        this.signedUrlUpload$.next(response);
      },
    });

    this.signedUrl$.pipe(takeUntilDestroyed()).subscribe({
      next: (data) => {
        if (environment.local) {
          this.putFile(data.response.uploadResponse.process.details.fileInformation);
        }
      },
    });

    this.deleteFile$.pipe(takeUntilDestroyed()).subscribe({
      next: (data) => {
        this.deleteFile(data);
      },
    });
  }

  private getSignedUrl(file: FileDataUpload) {
    return this.uploadService.getSignedUrl(file);
  }

  private uploadFileWithSignedUrl(file: File, signedUrl: string) {
    return this.uploadService.uploadToSignedUrl(file, signedUrl);
  }

  private putFile(filename?: string) {
    this.uploadService.updateFiles(filename);
  }

  private deleteFile(fileData: FileData & {workflowId: string}): void {
    if (fileData.storedName) {
      this.uploadService.deleteFile(fileData.workflowId, fileData.storedName);
    }
  }
}
